import React, { useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import {
    GotvCampaignFooter,
    DEFAULT_SOCIAL_HEADER,
} from "QuorumGrassroots/widgets/GotvCampaignFooter/GotvCampaignFooter"
import { useGotvSocialSharing } from "QuorumGrassroots/widgets/GotvCampaignFooter/useGotvSocialSharing"
import { CampaignSubpageHeader } from "QuorumGrassroots/widgets/ReusableComponents/CampaignSubpageHeader"
import {
    SubpageContainer,
    LoadingContainer,
    Wrapper,
} from "QuorumGrassroots/pages/campaign/elected-officials/ElectedOfficialsPage.styles"
import { useCampaign, useElectedOfficials } from "QuorumGrassroots/services/grassrootsCampaign"
import { CampaignTitleSubtitle } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CampaignTitleSubtitle/CampaignTitleSubtitle"
import { CollapsibleSection } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/CollapsibleSection/CollapsibleSection"
import {
    catalogOfficialsByRole,
    buildOfficialCardProps,
    buildOfficialActions,
} from "QuorumGrassroots/pages/campaign/elected-officials/ElectedOfficialsPage.helper"
import { OfficialCard } from "QuorumGrassroots/widgets/ReusableComponents/OfficialCard"
import { LoadingSkeleton } from "QuorumGrassroots/campaign-forms/components/GotvV2Form/components/LoadingSkeleton/LoadingSkeleton"
import { EmptyState } from "QuorumGrassroots/widgets/ReusableComponents/EmptyState/EmptyState"
import {
    initializeFilters,
    FilterState,
} from "QuorumGrassroots/widgets/ReusableComponents/FilterButton/FilterButton.helper"
import { OfficialDetailsModal } from "QuorumGrassroots/widgets/ReusableComponents/OfficialDetailsModal"

export const ElectedOfficialsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()

    const [filters, setFilters] = useState<FilterState>(initializeFilters())
    const [officialDetailsId, setOfficialDetailsId] = useState<string | null>(null)

    const { data: campaign, isLoading: isCampaignLoading, isError: isCampaignError } = useCampaign(id)
    const { data: electedOfficials, isLoading: isElectedOfficialsLoading } = useElectedOfficials()

    const selectedOfficial = useMemo(() => {
        if (!officialDetailsId) return null
        return electedOfficials.find((official) => official.id === officialDetailsId) ?? null
    }, [officialDetailsId, electedOfficials])

    const filteredOfficials = useMemo(() => {
        if (!electedOfficials) return []
        return electedOfficials.filter((candidate) => filters.party[candidate.party.toLowerCase()])
    }, [electedOfficials, filters])

    const catalogedOfficialsByRole = useMemo(() => {
        if (!filteredOfficials) return []
        return catalogOfficialsByRole(filteredOfficials)
    }, [filteredOfficials])

    const isLoading = isCampaignLoading || isElectedOfficialsLoading

    const socialPlatforms = useGotvSocialSharing({
        socialMediaPlatforms: campaign?.social_media_platforms,
        shareUrl: window.location.href,
        shareText: campaign?.social_share_text ?? `Help ${campaign?.name} get out the vote!`,
        imageUrl: campaign?.social_image_url,
    })

    if (isLoading)
        return (
            <Wrapper>
                <LoadingContainer>
                    <LoadingSkeleton />
                </LoadingContainer>
            </Wrapper>
        )

    if (isCampaignError)
        return (
            <Wrapper>
                <SubpageContainer>
                    <p>Error loading campaign. Please try again later.</p>
                </SubpageContainer>
            </Wrapper>
        )

    return (
        <SubpageContainer>
            <CampaignTitleSubtitle campaign={campaign} />
            <CampaignSubpageHeader
                title="Review Elected Officials"
                backUrl={`/campaign/${id}`}
                dataCy="elected-officials-header"
                withExtraMarginBottom
                showFilterButton
                onFiltersChange={(filter: FilterState) => {
                    setFilters(filter)
                }}
            />
            {catalogedOfficialsByRole.length > 0 ? (
                catalogedOfficialsByRole.map((role) => (
                    <CollapsibleSection key={role.name} title={role.name}>
                        {role.officials.map((official) => (
                            <OfficialCard
                                key={official.id}
                                official={buildOfficialCardProps(official)}
                                actions={buildOfficialActions(official)}
                                data-plum-id={`elected-officials.${official.id}`}
                                onClick={() => setOfficialDetailsId(official.id)}
                            />
                        ))}
                    </CollapsibleSection>
                ))
            ) : (
                <EmptyState
                    icon="fa-users"
                    title="No elected officials"
                    description="No elected officials were found for this election"
                    dataCy="elected-officials-empty-state"
                />
            )}

            <GotvCampaignFooter
                socialPlatforms={socialPlatforms}
                socialHeading={campaign.social_header ?? DEFAULT_SOCIAL_HEADER}
            />

            {selectedOfficial && (
                <OfficialDetailsModal
                    official={selectedOfficial}
                    isOpen={selectedOfficial !== null}
                    onClose={() => setOfficialDetailsId(null)}
                />
            )}
        </SubpageContainer>
    )
}
